import { useState, useCallback } from "react";

/**
 * Custom hook for phone number validation
 * Validates South African phone numbers and provides error messages
 */
export const usePhoneValidation = () => {
  const [phoneError, setPhoneError] = useState("");

  // South African phone number regex patterns
  const phonePatterns = {
    // +27XXXXXXXXX format
    international: /^\+27[0-9]{9}$/,
    // 0XXXXXXXXX format
    national: /^0[0-9]{9}$/,
    // XXXXXXXXX format (without leading 0)
    local: /^[0-9]{9}$/,
  };

  // Validate phone number
  const validatePhone = useCallback((phone) => {
    if (!phone) {
      setPhoneError("Phone number is required");
      return false;
    }

    // Remove any spaces, dashes, or parentheses
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, "");

    // Check if it matches any valid pattern
    const isValid = Object.values(phonePatterns).some((pattern) =>
      pattern.test(cleanPhone)
    );

    if (!isValid) {
      setPhoneError(
        "Please enter a valid South African phone number (e.g., +27123456789 or 0123456789)"
      );
      return false;
    }

    // Clear error if valid
    setPhoneError("");
    return true;
  }, []);

  // Format phone number to international format
  const formatPhone = useCallback((phone) => {
    if (!phone) return "";

    // Remove any spaces, dashes, or parentheses
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, "");

    // If it's already in international format, return as is
    if (phonePatterns.international.test(cleanPhone)) {
      return cleanPhone;
    }

    // If it's in national format (0XXXXXXXXX), convert to international
    if (phonePatterns.national.test(cleanPhone)) {
      return `+27${cleanPhone.substring(1)}`;
    }

    // If it's in local format (XXXXXXXXX), add +27
    if (phonePatterns.local.test(cleanPhone)) {
      return `+27${cleanPhone}`;
    }

    // If none of the above, return original
    return phone;
  }, []);

  // Clear phone error
  const clearPhoneError = useCallback(() => {
    setPhoneError("");
  }, []);

  // Check if phone number is valid without setting error
  const isValidPhone = useCallback((phone) => {
    if (!phone) return false;
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, "");
    return Object.values(phonePatterns).some((pattern) =>
      pattern.test(cleanPhone)
    );
  }, []);

  return {
    phoneError,
    validatePhone,
    formatPhone,
    clearPhoneError,
    isValidPhone,
  };
};

